import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { X, Trash2, Minus, Plus, ArrowRight } from 'lucide-react'
import { debounce, formatCurrency } from '@utils/helpers'
import { calculateItemPrice, calculateItemRelatedOriginalPrice } from '@utils/currency'
import { getPlaceholderImageUrl } from '@utils/image'
import { useSessionStore } from '@/stores'
import { useCartStore } from '@/stores/useCartStore'

interface CartProps {
  onClose: () => void
  hideHeader?: boolean
}

export const Cart = ({ onClose, hideHeader }: CartProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isOnCheckout = location.pathname.includes('/checkout')
  const session = useSessionStore(s => s.session)
  const isSessionUserKnown = useSessionStore(s => s.isSessionUserKnown)()
  const clearStepStack = useSessionStore(s => s.clearStepStack)

  const cart = useCartStore(s => s.cart)
  const updateCartItem = useCartStore(s => s.updateCartItem)
  const updateSessionCart = useCartStore(s => s.updateSessionCart)
  const setConfirmCheckoutOpen = useCartStore(s => s.setConfirmCheckoutOpen)

  const sessionId = session?.id

  const debouncedSyncCart = useMemo(
    () =>
      debounce(() => {
        if (sessionId) {
          updateSessionCart(sessionId).catch(() => {})
        }
      }, 500),
    [sessionId, updateSessionCart]
  )

  const currency = (session as any)?.currency || (cart[0]?.campaign_product as any)?.currency || 'USD'

  const getItemVariants = (item: any): string => {
    const opts = item.variant?.options
    if (!opts?.length) {
      return ''
    }
    return opts.map((o: any) => `${o.name}: ${o.value}`).join(', ')
  }

  const getImageUrl = (item: any): string => {
    const cp = item.campaign_product
    return (
      cp?.image?.file?.medium ||
      cp?.image?.file?.original ||
      cp?.product?.default_image?.file?.medium ||
      cp?.product?.default_image?.file?.original ||
      cp?.product?.images?.[0]?.file?.medium ||
      cp?.product?.images?.[0]?.file?.original ||
      getPlaceholderImageUrl('medium')
    )
  }

  const getItemPrice = (item: any): number => calculateItemPrice(item, session)

  const cartSubtotal = useMemo(
    () => cart.reduce((sum: number, it: any) => sum + calculateItemPrice(it, session), 0),
    [cart, session]
  )

  const handleQty = useCallback(
    (index: number, qty: number) => {
      useCartStore.getState().cancelCartAutoClose()
      const item = cart[index]
      if (!item) {
        return
      }
      const max = (item.campaign_product as any)?.max_quantity || 10
      const nextQty = Math.min(Math.max(0, qty), max)
      updateCartItem(index, { quantity: nextQty })
      debouncedSyncCart()
    },
    [cart, updateCartItem, debouncedSyncCart]
  )

  const goCheckout = useCallback(() => {
    useCartStore.getState().cancelCartAutoClose()
    if (isSessionUserKnown) {
      clearStepStack()
      onClose()
      navigate('/step/checkout')
    } else {
      setConfirmCheckoutOpen(true)
    }
  }, [isSessionUserKnown, clearStepStack, navigate, onClose, setConfirmCheckoutOpen])

  const handleContinue = useCallback(() => {
    useCartStore.getState().cancelCartAutoClose()
    onClose()
    if (isOnCheckout) {
      navigate('/products')
    }
  }, [isOnCheckout, navigate, onClose])

  if (!cart?.length) {
    return (
      <div className="cart-empty">
        <p>Your cart is empty</p>
      </div>
    )
  }

  const showTotal = cart.length > 1

  return (
    <div className="cart" onPointerEnter={() => useCartStore.getState().cancelCartAutoClose()}>
      {!hideHeader && (
        <div className="cart-top-bar">
          <h3>Your Cart</h3>
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(0,0,0,0.06)' }}
            whileTap={{ scale: 0.9 }}
            className="btn-bare close">
            <X size={18} />
          </motion.button>
        </div>
      )}

      <div className="items">
        <AnimatePresence>
          {cart.map((item: any, index: number) => {
            const max = (item.campaign_product as any)?.max_quantity || 10
            const relatedOriginalPrice = calculateItemRelatedOriginalPrice(item)
            return (
              <motion.div
                key={`cart-${item.campaign_product?.id || index}-${item.related_product?.id || 0}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
                className="item">
                <img
                  className="image"
                  src={getImageUrl(item)}
                  onError={e => {
                    ;(e.target as HTMLImageElement).src = getPlaceholderImageUrl('medium')
                  }}
                  alt={item.campaign_product.name}
                />

                <div className="info">
                  <div className="flex justify-between items-start gap-1">
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        className="btn-bare product-name-link no-transform name"
                        onClick={() => {
                          const cp = item.campaign_product as any
                          const id = cp?.id
                          const slug = cp?.slug || id
                          if (id) {
                            onClose()
                            navigate(`/products/${id}/${slug}`)
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: item.campaign_product.name }}
                      />
                      {getItemVariants(item) && (
                        <div className="text-fluid-xs mt-0.5" style={{ color: 'var(--light-text-color)' }}>
                          {getItemVariants(item)}
                        </div>
                      )}
                    </div>
                    <button onClick={() => handleQty(index, 0)} className="btn-bare no-transform remove" title="Remove">
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-1.5">
                    <div className="qty-pill qty">
                      <button
                        className="btn-bare no-transform"
                        disabled={item.quantity <= 1 || max === 1}
                        onClick={() => handleQty(index, item.quantity - 1)}>
                        <Minus size={11} />
                      </button>
                      <div className="count">{item.quantity}</div>
                      <button
                        className="btn-bare no-transform"
                        disabled={item.quantity >= max}
                        onClick={() => handleQty(index, item.quantity + 1)}>
                        <Plus size={11} />
                      </button>
                    </div>
                    {relatedOriginalPrice != null ? (
                      <span className="price flex items-baseline gap-1.5">
                        <span className="line-through font-normal" style={{ color: 'var(--light-text-color)' }}>
                          {formatCurrency(relatedOriginalPrice, currency)}
                        </span>
                        <span className="text-red-500">{formatCurrency(getItemPrice(item), currency)}</span>
                      </span>
                    ) : (
                      <span className="price">{formatCurrency(getItemPrice(item), currency)}</span>
                    )}
                  </div>

                  {item.quantity >= max && <p className="max-warn">*Max limit of {max}</p>}
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

      <div className="totals">
        {showTotal && (
          <div className="cart-totals-row w-full flex justify-between items-center px-4 py-2 font-semibold text-fluid-base">
            <span>Total</span>
            <span>{formatCurrency(cartSubtotal, currency)}</span>
          </div>
        )}
        <button onClick={handleContinue} className="continue-cta">
          Continue Shopping
        </button>
        {!isOnCheckout && (
          <button onClick={goCheckout} className="checkout-cta">
            Checkout
            <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  )
}

export default Cart
